import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Briefcase, Clock, Play, CheckCircle } from 'lucide-react';

const OutstandingWorkReport = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({
    scheduled: { count: 0, value: 0 },
    inProgress: { count: 0, value: 0 },
    total: { count: 0, value: 0 },
    avgDuration: 0
  });

  useEffect(() => {
    loadJobsData();
  }, []);

  const loadJobsData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          start_date,
          completion_date,
          status,
          total_amount,
          job_address,
          job_city,
          customer_id,
          customers (
            first_name,
            last_name,
            company,
            phone
          )
        `)
        .in('status', ['Scheduled', 'In Progress'])
        .order('start_date', { ascending: true });

      if (error) throw error;

      const jobsData = data || [];
      const scheduled = jobsData.filter(j => j.status === 'Scheduled');
      const inProgress = jobsData.filter(j => j.status === 'In Progress');

      // Calculate average duration for completed jobs
      const { data: completedJobs } = await supabase
        .from('jobs')
        .select('start_date, completion_date')
        .eq('status', 'Completed')
        .not('start_date', 'is', null)
        .not('completion_date', 'is', null)
        .limit(50);

      let totalDays = 0;
      let countWithDates = 0;
      completedJobs?.forEach(job => {
        if (job.start_date && job.completion_date) {
          const start = new Date(job.start_date);
          const end = new Date(job.completion_date);
          const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
          if (days >= 0) {
            totalDays += days;
            countWithDates++;
          }
        }
      });

      setSummary({
        scheduled: {
          count: scheduled.length,
          value: scheduled.reduce((sum, j) => sum + parseFloat(j.total_amount || 0), 0)
        },
        inProgress: {
          count: inProgress.length,
          value: inProgress.reduce((sum, j) => sum + parseFloat(j.total_amount || 0), 0)
        },
        total: {
          count: jobsData.length,
          value: jobsData.reduce((sum, j) => sum + parseFloat(j.total_amount || 0), 0)
        },
        avgDuration: countWithDates > 0 ? Math.round(totalDays / countWithDates) : 0
      });

      setJobs(jobsData);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilStart = (startDate) => {
    if (!startDate) return null;
    const today = new Date();
    const start = new Date(startDate);
    const days = Math.floor((start - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getDaysInProgress = (startDate) => {
    if (!startDate) return 0;
    const today = new Date();
    const start = new Date(startDate);
    const days = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-CA');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Scheduled': { color: 'bg-blue-100 text-blue-800', icon: Clock },
      'In Progress': { color: 'bg-yellow-100 text-yellow-800', icon: Play }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: Briefcase };
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon size={14} />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p>Loading outstanding work report...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Print Header */}
      <div className="print-only text-center mb-6">
        <h1 className="text-2xl font-bold">JFT Works</h1>
        <h2 className="text-xl">Outstanding Work Report</h2>
        <p className="text-sm text-gray-600">
          As of {new Date().toLocaleDateString('en-CA')}
        </p>
      </div>

      {/* Screen Header */}
      <div className="no-print mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Briefcase size={32} />
          Outstanding Work Report
        </h1>
        <p className="text-gray-600 mt-2">Active jobs and workload status</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Scheduled Jobs</h3>
            <Clock className="text-blue-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{summary.scheduled.count}</div>
          <p className="text-sm text-gray-600 mt-1">{formatCurrency(summary.scheduled.value)}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">In Progress</h3>
            <Play className="text-yellow-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{summary.inProgress.count}</div>
          <p className="text-sm text-gray-600 mt-1">{formatCurrency(summary.inProgress.value)}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Active</h3>
            <Briefcase className="text-green-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{summary.total.count}</div>
          <p className="text-sm text-gray-600 mt-1">{formatCurrency(summary.total.value)}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Avg. Job Duration</h3>
            <CheckCircle className="text-purple-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{summary.avgDuration}</div>
          <p className="text-sm text-gray-600 mt-1">days</p>
        </div>
      </div>

      {/* Jobs Table */}
      {jobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
          <p className="text-xl font-semibold text-gray-900">No outstanding work!</p>
          <p className="text-gray-600 mt-2">All jobs are completed.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timeline</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {jobs.map((job) => {
                  const daysUntilStart = getDaysUntilStart(job.start_date);
                  const daysInProgress = getDaysInProgress(job.start_date);

                  return (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{job.job_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">
                          {job.customers?.first_name} {job.customers?.last_name}
                          {job.customers?.company && ` (${job.customers.company})`}
                        </div>
                        <div className="text-sm text-gray-500">{job.customers?.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">{job.job_address}</div>
                        <div className="text-sm text-gray-500">{job.job_city}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(job.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {formatDate(job.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {job.status === 'Scheduled' && daysUntilStart !== null && (
                          <span className={`text-sm ${daysUntilStart < 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {daysUntilStart < 0 ? `${Math.abs(daysUntilStart)} days overdue` :
                             daysUntilStart === 0 ? 'Starts today' :
                             `In ${daysUntilStart} days`}
                          </span>
                        )}
                        {job.status === 'In Progress' && (
                          <span className="text-sm text-yellow-600">
                            {daysInProgress} days in progress
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">
                        {formatCurrency(job.total_amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-right font-semibold text-gray-900">
                    Total:
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-lg text-gray-900">
                    {formatCurrency(summary.total.value)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body, html, * {
            background: white !important;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OutstandingWorkReport;
